import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ShieldCheck, ShieldX, Phone, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminOverrideReviewModal — Intermediary popup the admin sees when
 * they click "View Request" on an `admin_override_request`
 * notification. Shows the user + supervisor + factors and exposes
 * Approve / Reject controls.
 *
 *   • Approve  → navigate to /admin/users?override_request_id=<id>
 *                (UserAdminPage handles the rest: opens Edit dialog,
 *                jumps to Security tab, BypassSection prefilled).
 *   • Reject   → in-place textarea for rationale (≥3 chars) → POST
 *                /admin/override-requests/{id}/reject → toast & close.
 *
 * Trigger via global window event `open-admin-override-review` with
 * detail.override_request_id — fired by NotificationsPage when admin
 * clicks the notification's View Request action.
 */
export default function AdminOverrideReviewModal() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [ovrId, setOvrId] = useState(null);
  const [ovr, setOvr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.override_request_id;
      if (id) setOvrId(id);
    };
    window.addEventListener('open-admin-override-review', handler);
    return () => window.removeEventListener('open-admin-override-review', handler);
  }, []);

  useEffect(() => {
    if (!ovrId || user?.role !== 'admin') { setOvr(null); return; }
    setLoading(true);
    api.get(`/admin/override-requests/${ovrId}`)
      .then((r) => setOvr(r.data))
      .catch(() => { toast.error('Could not load override request'); setOvrId(null); })
      .finally(() => setLoading(false));
  }, [ovrId, user?.role, api]);

  const close = () => { setOvrId(null); setOvr(null); setRejecting(false); setRationale(''); };

  const approve = () => {
    close();
    navigate(`/admin/users?override_request_id=${ovrId}`);
  };

  const submitReject = async () => {
    if (rationale.trim().length < 3) { toast.error('Rationale required (3+ chars)'); return; }
    setSubmitting(true);
    try {
      await api.post(`/admin/override-requests/${ovrId}/reject`, { rationale: rationale.trim() });
      toast.success('Override rejected; supervisor notified');
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally { setSubmitting(false); }
  };

  if (user?.role !== 'admin') return null;

  const factors = ovr?.factors || {};
  const factorList = [
    factors.mfa && 'MFA',
    factors.recovery_key && 'Recovery Key',
  ].filter(Boolean).join(' + ') || '—';
  const isFinal = ovr && ['approved', 'approved_notified', 'rejected', 'consumed'].includes(ovr.status);

  return (
    <Dialog open={!!ovrId} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="admin-override-review-modal"
        // Per spec: the overview popup must stay open when the user
        // opens the contact bottom-sheet (call/email) on top of it.
        // Block outside-clicks and Escape so the ONLY way to dismiss
        // is the top-right X button rendered by shadcn DialogContent.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Review Admin Override Request</DialogTitle>
          <DialogDescription className="text-xs">
            A supervisor has certified a user's identity and is asking you to issue a single-use override password.
          </DialogDescription>
        </DialogHeader>
        {loading || !ovr ? (
          <p className="text-xs text-center py-4 text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-3 text-sm">
            <Card>
              <CardContent className="p-3 text-xs space-y-1.5">
                <p className="font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">User</p>
                <KV label="Name" value={ovr.requester_name} />
                <KV label="Role" value={ovr.requester_role} />
                <KV label="State" value={ovr.supervisor_state /* requester state is on user — fallback for clarity */} />
                <KV label="Personal Phone" value={ovr.requester_phone} icon="phone" contactName={ovr.requester_name} />
                <KV label="Personal Email" value={ovr.requester_email} icon="email" contactName={ovr.requester_name} />
                <KV label="ID Number" value={ovr.requester_id_number} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-xs space-y-1.5">
                <p className="font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">Supervisor</p>
                <KV label="Name" value={ovr.supervisor_name} />
                <KV label="State" value={ovr.supervisor_state} />
                <KV label="Certified" value={ovr.supervisor_certified ? 'Yes — identified the user' : 'No'} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-xs space-y-1.5">
                <p className="font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">Credentials to bypass</p>
                <p className="font-semibold" data-testid="admin-override-review-factors">{factorList}</p>
              </CardContent>
            </Card>

            {isFinal ? (
              <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-300 p-2 text-[11px] text-amber-900 dark:text-amber-200 text-center" data-testid="admin-override-review-finalized">
                This request is already <span className="font-semibold">{ovr.status.replace('_', ' ')}</span>. No further action available.
              </div>
            ) : rejecting ? (
              <div className="space-y-2">
                <Textarea
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Explain why you're rejecting (the supervisor will see this)…"
                  className="text-xs min-h-[80px]"
                  data-testid="admin-override-reject-rationale"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRejecting(false)} disabled={submitting} data-testid="admin-override-reject-cancel">
                    Back
                  </Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={submitReject} disabled={submitting} data-testid="admin-override-reject-submit">
                    {submitting ? 'Submitting…' : 'Submit Rejection'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setRejecting(true)} data-testid="admin-override-reject-btn">
                  <ShieldX className="w-4 h-4 mr-1.5" /> Reject Bypass
                </Button>
                <Button size="sm" className="bg-[#586D52] hover:bg-[#4a5d46] text-white" onClick={approve} data-testid="admin-override-approve-btn">
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve Bypass
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value, icon, contactName }) {
  const dispatchSheet = (mode) => {
    if (!value) return;
    window.dispatchEvent(new CustomEvent('open-contact-sheet', {
      detail: { mode, value, contactName },
    }));
  };
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="flex-1 font-medium break-all flex items-center gap-1.5">
        <span data-testid={`override-review-kv-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          {value || <span className="text-muted-foreground italic">Not on file</span>}
        </span>
        {value && icon === 'phone' && (
          <button onClick={() => dispatchSheet('call')} aria-label="Call" className="ml-1 p-1 rounded hover:bg-muted" data-testid={`override-review-call-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <Phone className="w-3.5 h-3.5 text-[#586D52]" />
          </button>
        )}
        {value && icon === 'email' && (
          <button onClick={() => dispatchSheet('email')} aria-label="Email" className="ml-1 p-1 rounded hover:bg-muted" data-testid={`override-review-email-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <Mail className="w-3.5 h-3.5 text-[#586D52]" />
          </button>
        )}
      </span>
    </div>
  );
}
